import type { IInputQuestion } from '../../Questions/_question';
import type { InputHTMLAttributes } from 'react';
import type { FieldError } from 'react-hook-form';

import {
  faCheck,
  faExclamationCircle,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';

import ErrorMessage from '../../ErrorMessage';

import IconContainer from './IconContainer';
import InputContainer from './InputContainer';
import MaskedInput, { getBlocksFromMask } from './MaskedInput';
import MessageContainer from './MessageContainer';
import StatusIconContainer from './StatusIconContainer';
import StyledInput from './StyledInput';
import SuccessMessage from './SuccessMessage';

import theme from '$/assets/theme';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  name: string;
  placeholder?: string;
  error?: boolean | FieldError;
  errorMessage?: IInputQuestion['errorMessage'];
  success?: boolean;
  successMessage?: string | false;
  containerProps?: object;
  icon?: React.ReactNode;
  iconPosition?: 'end' | 'start';
  disabled?: boolean;
  disableError?: boolean;
  inputProps?: object;
  errorMessageColor?: string;
  maskedInput?: boolean;
  hideErrorIcon?: boolean;
  datePickerSelected?: boolean;
}

const TextInput = React.forwardRef<
  React.Ref<HTMLInputElement> | undefined,
  InputProps
>(
  (
    {
      containerProps = {},
      inputProps = {},
      iconPosition,
      errorMessageColor = theme.colors.functionalRed,
      name,
      hideErrorIcon = false,
      ...props
    },
    ref
  ) => (
    <>
      <InputContainer
        iconPosition={props.icon ? iconPosition : undefined}
        {...containerProps}
        disabled={props.disabled}
      >
        {props.icon && <IconContainer {...props}>{props.icon}</IconContainer>}
        {props.maskedInput ? (
          <MaskedInput
            mask={props?.placeholder ? props.placeholder : ''}
            lazy={false}
            blocks={getBlocksFromMask(props.placeholder)}
            iconPosition={iconPosition}
            name={name}
            inputRef={ref}
            {...inputProps}
            {...props}
          />
        ) : (
          <StyledInput
            role={'input'}
            onWheel={(e: React.ChangeEvent<HTMLInputElement>) =>
              e.target.blur()
            }
            iconPosition={iconPosition}
            name={name}
            {...props}
            {...inputProps}
            ref={ref}
          />
        )}
      </InputContainer>
      {!props.disableError && (
        <MessageContainer
          show={
            !!(
              (props.error && props.errorMessage) ||
              (props.success && props.successMessage)
            )
          }
        >
          {props.error && !props.success && (
            <>
              {!hideErrorIcon && (
                <StatusIconContainer>
                  <FontAwesomeIcon
                    icon={faExclamationCircle}
                    color={errorMessageColor}
                    title={
                      typeof props.errorMessage === 'string'
                        ? props.errorMessage
                        : undefined
                    }
                  />
                </StatusIconContainer>
              )}
              {props.errorMessage && (
                <ErrorMessage color={errorMessageColor}>
                  {props.errorMessage}
                </ErrorMessage>
              )}
            </>
          )}
          {props.success && (
            <>
              <StatusIconContainer>
                <FontAwesomeIcon
                  icon={faCheck}
                  color={theme.colors.brandGreen}
                />
              </StatusIconContainer>
              {props.successMessage && (
                <SuccessMessage>{props.successMessage}</SuccessMessage>
              )}
            </>
          )}
        </MessageContainer>
      )}
    </>
  )
);

const defaultProps: InputProps = {
  name: '',
};

TextInput.defaultProps = defaultProps;

export default TextInput;
